import { useAvailableWithCustomTypesColumns } from "PFApp/components/table_properties/use_available_with_custom_types_columns";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import { WORKFLOW_ROLES_TABLE_SECTION_KEY } from "PFApp/workflow/parts/roles/roles_constants";
import { TableColumn } from "PFTypes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export const useRolesColumns = (blockedProperties: string[] = []) => {
  const { t } = useTranslation("workflow", { keyPrefix: "parts.roles" });
  const roleTemplate = useTemplate(TemplateKey.Role);

  const availableColumns = useMemo<TableColumn[]>(
    () => [
      {
        name: "name",
        title: t("roleTitle"),
        locked: true,
        size: 3,
        type: "text",
        orderKey: "activity.name",
        isDefault: true
      },
      {
        name: "interested",
        title: t("interested"),
        size: 1,
        type: "checkmark",
        workforceMemberView: true,
        locked: true
      },
      {
        name: "shortlist_status",
        title: t("shortlistStatus"),
        size: 2,
        type: "shortlistStatus",
        workforceMemberView: true,
        locked: true
      },
      { name: "status", title: t("status"), size: 2, type: "text" },
      { name: "workflow_state", title: t("workflowState"), size: 2, type: "text" },
      { name: "id", title: t("id"), size: 1, type: "text" },
      {
        name: "post_until",
        title: t("expiryDate"),
        size: 2,
        type: "date-time",
        orderKey: "activity.post_until"
      },
      {
        name: "assignee",
        title: t("assignee"),
        size: 2,
        type: "profile",
        attribute: ["assignee", "profile"],
        orderKey: "relation.activity_has_assignees.name"
      },
      { name: "vacancies", title: t("vacanciesOpenTotal"), size: 1, type: "text" },
      {
        name: "availability",
        title: t("availabilityRequirement"),
        size: 3,
        type: "availability",
        orderKey: "activity.start_date"
      },
      {
        name: "due",
        attribute: ["post_until"],
        title: t("dueInDays"),
        size: 2,
        type: "text",
        orderKey: "activity.post_until"
      },
      {
        name: "created_at",
        title: t("dateCreated"),
        size: 2,
        type: "date",
        orderKey: "activity.saas_created_at"
      },
      {
        name: "start_date",
        title: t("startDate"),
        size: 1,
        type: "date",
        orderKey: "activity.start_date"
      },
      {
        name: "principal_owner",
        title: t("principalOwner"),
        size: 2,
        type: "principalOwner"
      },
      {
        name: "engagement",
        title: t("engagement"),
        size: 2,
        type: "text",
        orderKey: "activity.engagement.name"
      },
      {
        name: "source_role",
        title: t("sourceRole"),
        size: 2,
        type: "rollforward_source"
      },
      {
        name: "target_roles",
        title: t("targetRoles"),
        size: 2,
        type: "rollforward_targets"
      }
    ],
    [t]
  );

  const { availableWithCustomTypesColumns } = useAvailableWithCustomTypesColumns({
    blockedProperties,
    template: roleTemplate,
    availableColumns,
    sectionKey: WORKFLOW_ROLES_TABLE_SECTION_KEY
  });

  return { availableColumns, availableWithCustomTypesColumns };
};
